import type { UserStatus } from '@/types/api';
import i18n from '@/i18n';

/**
 * Combine a user's declared status (their persistent ONLINE/IDLE/DND/INVISIBLE
 * preference) with the LIVE socket-presence flag into the status to display.
 *
 * `online` is the live presence signal: `true`/`false` from a presence:update
 * event, or `undefined` when we have no live signal yet (lean on the
 * server-sent status, which is already presence-corrected — OFFLINE for any
 * user with no open socket). The live flag is authoritative for the on/off
 * decision: a fresh `online === true` must override a stale OFFLINE, otherwise
 * a peer who just reconnected would stay greyed-out until the next refetch.
 */
export function resolveDisplayedStatus(
  declaredStatus: UserStatus | null | undefined,
  online: boolean | undefined,
  isSelf = false,
): UserStatus {
  // I always see my own declared status (including INVISIBLE).
  if (isSelf) return declaredStatus ?? (online ? 'ONLINE' : 'OFFLINE');
  // INVISIBLE means "appear offline" to everyone else, even while connected.
  if (declaredStatus === 'INVISIBLE') return 'OFFLINE';
  // Live presence wins the on/off call.
  if (online === true) {
    return declaredStatus === 'IDLE' || declaredStatus === 'DND' ? declaredStatus : 'ONLINE';
  }
  if (online === false) return 'OFFLINE';
  // No live signal — trust the server's (presence-corrected) declared status.
  if (!declaredStatus || declaredStatus === 'OFFLINE') return 'OFFLINE';
  return declaredStatus;
}

export function presenceLabel(status: UserStatus): string {
  switch (status) {
    case 'ONLINE':
      return i18n.t('lib2.presence.online');
    case 'IDLE':
      return i18n.t('lib2.presence.idle');
    case 'DND':
      return i18n.t('lib2.presence.dnd');
    case 'INVISIBLE':
      return i18n.t('lib2.presence.invisible');
    case 'OFFLINE':
    default:
      return i18n.t('lib2.presence.offline');
  }
}

/**
 * Short subtitle shown under the status name in the picker. Mirrors the
 * Discord "what does this mean" hint and keeps users from guessing the
 * difference between IDLE / DND / INVISIBLE.
 */
export function presenceDescription(status: UserStatus): string {
  switch (status) {
    case 'ONLINE':
      return i18n.t('lib2.presence.onlineDesc');
    case 'IDLE':
      return i18n.t('lib2.presence.idleDesc');
    case 'DND':
      return i18n.t('lib2.presence.dndDesc');
    case 'INVISIBLE':
      return i18n.t('lib2.presence.invisibleDesc');
    case 'OFFLINE':
    default:
      return i18n.t('lib2.presence.offlineDesc');
  }
}

export function presenceDotClass(status: UserStatus): string {
  switch (status) {
    case 'ONLINE':
      return 'presence-mark presence-online bg-success';
    case 'IDLE':
      return 'presence-mark presence-idle bg-warning';
    case 'DND':
      return 'presence-mark presence-dnd bg-danger';
    case 'INVISIBLE':
    case 'OFFLINE':
    default:
      return 'presence-mark presence-offline bg-ink-muted';
  }
}
